import { type ReactNode, useEffect, useRef, useState } from 'react';
import {
  useActiveWorkspaceId,
  useWorkspaceBillingSummary,
} from '~/stores/workspace';
import { useBillingUpgrade } from '~/features/billing/BillingUpgradeProvider';
import { hasActivePaidSubscription } from '~/features/billing/generationBillingAccess';
import { privateAssetUrl } from '~/repositories/assetRepository';
import { ImageWatermarkContext } from './imageWatermarkContext';

export const ImageWatermarkProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const workspaceId = useActiveWorkspaceId() ?? undefined;
  const { summary, refresh } = useWorkspaceBillingSummary(workspaceId);
  const { openUpgrade } = useBillingUpgrade();
  const key = `mylo:image-watermark:v1:${workspaceId}`;
  const [preference, setPreference] = useState(() => {
    try {
      return localStorage.getItem(key) === 'hidden';
    } catch {
      return false;
    }
  });
  const [now, setNow] = useState(Date.now);
  const subscription = summary?.subscription;
  const expiresAt =
    subscription?.paidThroughAt ?? subscription?.currentPeriodEnd;
  useEffect(() => {
    if (!expiresAt) return undefined;
    const delay = new Date(expiresAt).getTime() - Date.now();
    if (delay <= 0) return undefined;
    const timer = window.setTimeout(
      () => {
        setNow(Date.now());
        refresh();
      },
      Math.min(delay + 1, 2_147_483_647)
    );
    return () => window.clearTimeout(timer);
  }, [expiresAt, refresh]);
  const eligible = Boolean(
    subscription &&
    ['PRO', 'MAX', 'ENTERPRISE'].includes(subscription.plan) &&
    hasActivePaidSubscription(subscription, now)
  );
  const urls = useRef(new Map<string, string>());
  const hidden = eligible && preference;
  const updatePreference = (value: boolean) => {
    setPreference(value);
    try {
      localStorage.setItem(key, value ? 'hidden' : 'visible');
    } catch {
      /* Storage can be disabled. */
    }
  };
  return (
    <ImageWatermarkContext.Provider
      value={{
        workspaceId,
        originalUrl: assetId =>
          privateAssetUrl(workspaceId!, assetId, false, true),
        downloadUrl: assetId =>
          privateAssetUrl(workspaceId!, assetId, true, hidden),
        hidden,
        toggle: () => {
          if (!eligible) {
            openUpgrade({ reason: 'subscription', feature: 'watermark' });
            return;
          }
          updatePreference(!hidden);
        },
        register: (url, assetId) => {
          urls.current.set(url, assetId);
        },
        assetForUrl: url => urls.current.get(url),
        rejectOriginal: () => {
          updatePreference(false);
          refresh();
        },
      }}
    >
      {children}
    </ImageWatermarkContext.Provider>
  );
};
